import { isRestrictedShipping } from '@/lib/functions/isRestrictedShipping';
import { PARTY_SUPPLIES_LARGE_BOX, ShippingBoxSpec } from '@/lib/constants/shippingBoxes';

export interface ProductShippingDimensions {
  weight?: number | null;
  width?: number | null;
  height?: number | null;
  depth?: number | null;
}

/**
 * Matches Sylius ProductLoader: any missing or zero dimension/weight is not shippable.
 * Returns false when dimension data is unavailable (cannot confirm shippable).
 */
export function hasMissingShippingDimensions(dimensions?: ProductShippingDimensions | null): boolean {
  if (!dimensions) {
    return false;
  }

  const values = [
    dimensions.weight,
    dimensions.width,
    dimensions.height,
    dimensions.depth,
  ];

  if (values.every((value) => value === undefined || value === null)) {
    return false;
  }

  return values.some((value) => value === null || value === undefined || value === 0);
}

/**
 * Returns true when the item cannot fit in the given box at any rotation.
 */
export function exceedsShippingBox(
  dimensions?: ProductShippingDimensions | null,
  box: ShippingBoxSpec = PARTY_SUPPLIES_LARGE_BOX,
): boolean {
  if (!dimensions || hasMissingShippingDimensions(dimensions)) {
    return false;
  }

  const { weight, width, height, depth } = dimensions;
  if (
    weight == null ||
    width == null ||
    height == null ||
    depth == null ||
    weight <= 0 ||
    width <= 0 ||
    height <= 0 ||
    depth <= 0
  ) {
    return false;
  }

  if (weight > box.maxWeightLbs) {
    return true;
  }

  const itemDims = [width, height, depth].sort((a, b) => a - b);
  const boxDims = [box.lengthInches, box.widthInches, box.heightInches].sort((a, b) => a - b);

  return itemDims.some((dimension, index) => dimension > boxDims[index]);
}

/**
 * Oversized party-supply items with valid dimensions should be ordered by phone.
 */
export function requiresCallToOrder(
  shippingCategory?: string | null,
  dimensions?: ProductShippingDimensions | null,
): boolean {
  if (isRestrictedShipping(shippingCategory)) {
    return false;
  }

  if (hasMissingShippingDimensions(dimensions)) {
    return false;
  }

  return exceedsShippingBox(dimensions);
}

export function isProductShippable(
  shippingCategory?: string | null,
  dimensions?: ProductShippingDimensions | null,
): boolean {
  if (isRestrictedShipping(shippingCategory)) {
    return false;
  }

  if (hasMissingShippingDimensions(dimensions)) {
    return false;
  }

  if (exceedsShippingBox(dimensions)) {
    return false;
  }

  return true;
}
